import React, {FC, useEffect, useMemo, useState} from "react";
import {Box, FormHelperText, Typography} from "@material-ui/core";
import FfairPaper from "view/components/FfairPaper";
import Form, {
  ActionButton,
  Actions,
  BasicInput,
  Checkbox,
} from "view/components/Form";
import FfairModal from "view/components/modal/FfairModal";
import {useForm} from "react-hook-form";
import {ErrorType} from "ts/api/error";
import {unitSystems} from "constraints/general";

import {IExhibitorPair, IShow} from "@evennt-tech/api-v2-core";
import {IUpdateExhibitorBodyReq} from "@evennt-tech/api-v2-external";
import {AxiosError} from "axios";
import useStyles from "./styles";

type Props = {
  onClose: () => void;
  onSubmit: (values: IUpdateExhibitorBodyReq) => void;
  open: boolean;
  exhibitorPair: IExhibitorPair;
  show: IShow;
};

type FormValues = {
  standWidth: number;
  standLength: number;
  irregularArea: number;
  irregular: boolean;
};

const EditModal: FC<Props> = ({
  onClose,
  onSubmit,
  open,
  show,
  exhibitorPair,
}) => {
  const classes = useStyles();
  const unitsObj = unitSystems.find(({value}) => value === show.unitSystem);

  const [error, setError] = useState("");

  const defaultValues = useMemo(
    () => ({
      standWidth: exhibitorPair.stand?.size?.width || 0,
      standLength: exhibitorPair.stand?.size?.length || 0,
      irregularArea: exhibitorPair.stand?.size?.area || 0,
      irregular: exhibitorPair.stand?.size?.irregular || false,
    }),
    [exhibitorPair]
  );

  const form = useForm<FormValues>({
    mode: "all",
    defaultValues,
  });
  const {watch, setValue} = form;
  const fields = watch();
  const {standWidth, standLength, irregularArea, irregular} = fields;
  const handleSubmit = async () => {
    try {
      onSubmit({
        ...fields,
        exhibitorPairId: exhibitorPair.id,
        showId: exhibitorPair.showId,
      });
      onClose();
    } catch (error) {
      const axiosError = error as AxiosError;

      if (axiosError.response && axiosError?.response?.data?.message) {
        setError(axiosError?.response?.data?.message);
        return;
      }
      const typedError = error as ErrorType;
      if (typedError.message) {
        setError(typedError.message);
      }
    }
  };

  useEffect(() => {
    if (irregular) {
      return;
    }

    const standArea = Number((standLength * standWidth).toFixed(2));
    if (!standArea) {
      return;
    }
    setValue("irregularArea", standArea, {
      shouldValidate: true,
    });
  }, [standWidth, standLength, irregularArea, irregular, setValue]);

  const isDisabled = !form.formState.isValid || form.formState.isSubmitting;
  return (
    <FfairModal open={open} onClose={onClose}>
      <FfairPaper className={classes.paper}>
        <Typography variant="h2" className={classes.header}>
          Size
        </Typography>
        <Form form={form} onSubmit={() => null}>
          <BasicInput
            label={`Stand Width (${unitsObj?.units})`}
            name="standWidth"
            rules={{required: "required", valueAsNumber: true, min: 0}}
            InputProps={{
              inputProps: {
                min: 0,
                defaultValue: 0,
                step: "any",
              },
            }}
            type="number"
          />
          <BasicInput
            label={`Stand Depth (${unitsObj?.units})`}
            name="standLength"
            rules={{required: "required", valueAsNumber: true, min: 0}}
            InputProps={{
              inputProps: {
                min: 0,
                defaultValue: 0,
                step: "any",
              },
            }}
            type="number"
          />
          <BasicInput
            label={`Area (${unitsObj?.units}²)`}
            name="irregularArea"
            rules={{required: "required", valueAsNumber: true, min: 0}}
            InputLabelProps={{shrink: true}}
            InputProps={{
              readOnly: !irregular,
              inputProps: {
                min: 0,
                defaultValue: 0,
                step: "any",
              },
            }}
            type="number"
          />
          <Checkbox
            color="primary"
            size="small"
            label="Irregular"
            name="irregular"
            defaultChecked={Boolean(irregular)}
          />
          <Box mt={1}>
            <FormHelperText error>{error}</FormHelperText>
          </Box>
          <Actions>
            <ActionButton onClick={onClose} text="Cancel" />
            <ActionButton
              disabled={isDisabled}
              onClick={() => handleSubmit()}
              text="Save"
            />
          </Actions>
        </Form>
      </FfairPaper>
    </FfairModal>
  );
};

export default EditModal;
